import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PhoneIcon, CheckCircleIcon, XCircleIcon, ClockIcon, UserXIcon, EditIcon } from 'lucide-react';
import { EditGuardianContactModal } from './EditGuardianContactModal';
interface Guardian {
  id: string;
  name: string;
  phone?: string;
  whatsapp?: string;
}
interface Enrollment {
  id: string;
  classroomId: string;
  classroomName: string;
  academicYear: string;
  enrolledAt: string;
  leftAt?: string;
  status: 'active' | 'ended';
}
interface Student {
  id: string;
  name: string;
  email: string;
  externalStudentId?: string;
  status?: 'enrolled' | 'not-enrolled';
  leftOrganization?: boolean;
  createdAt?: string;
  guardian?: Guardian;
  enrollments?: Enrollment[];
  currentGrade?: string;
  joinedAt?: string;
}
interface StudentProfilePanelProps {
  isOpen: boolean;
  student: Student | null;
  onClose: () => void;
  onEndEnrollment?: (student: Student) => void;
  currentClassroom?: {
    name: string;
    academicYear: string;
  };
  showEndEnrollment?: boolean;
}
export const StudentProfilePanel: React.FC<StudentProfilePanelProps> = ({
  isOpen,
  student,
  onClose,
  onEndEnrollment,
  currentClassroom,
  showEndEnrollment = false
}) => {
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  if (!student) return null;
  const formatDate = (dateString: string) => {
    if (dateString === '-') return 'Not yet joined';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };
  const handleSaveGuardianContact = async (data: {
    guardianName?: string;
    guardianPhone: string;
  }) => {
    console.log('Saving guardian contact:', data, 'for student:', student.id);
    // TODO: Implement API call to save guardian contact
    await new Promise(resolve => setTimeout(resolve, 1000));
  };
  const handleRemoveGuardianContact = async () => {
    console.log('Removing guardian contact for student:', student.id);
    // TODO: Implement API call to remove guardian contact
    await new Promise(resolve => setTimeout(resolve, 1000));
  };
  return <AnimatePresence data-id="element-2298">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-40" onClick={onClose} data-id="element-2299" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-surface-elevated border-l border-custom shadow-2xl z-50 overflow-y-auto" data-id="element-2300">
            <div className="p-6 space-y-6" data-id="element-2301">
              {/* Header */}
              <div className="flex items-start justify-between" data-id="element-2302">
                <div className="flex items-center gap-4" data-id="element-2303">
                  <div className="w-16 h-16 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F] flex items-center justify-center font-bold text-xl" data-id="element-2304">
                    {student.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div data-id="element-2305">
                    <h2 className="text-xl font-bold text-text-primary" data-id="element-2306">
                      {student.name}
                    </h2>
                    <p className="text-sm text-text-secondary" data-id="element-2307">
                      {student.email}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" data-id="element-2308">
                  <XIcon size={20} className="text-text-secondary" data-id="element-2309" />
                </button>
              </div>

              {/* Basic Info */}
              {(student.externalStudentId || student.currentGrade || student.status || student.leftOrganization !== undefined || student.createdAt) && <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-2310">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide" data-id="element-2311">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-2312">
                    {student.externalStudentId && <div data-id="element-2313">
                        <p className="text-text-tertiary mb-1" data-id="element-2314">Student ID</p>
                        <p className="text-text-primary font-medium font-mono" data-id="element-2315">
                          {student.externalStudentId}
                        </p>
                      </div>}
                    {student.currentGrade && <div data-id="element-2316">
                        <p className="text-text-tertiary mb-1" data-id="element-2317">Current Grade</p>
                        <p className="text-text-primary font-medium" data-id="element-2318">
                          {student.currentGrade}
                        </p>
                      </div>}
                    {student.status && <div data-id="element-2319">
                        <p className="text-text-tertiary mb-1" data-id="element-2320">
                          Enrollment Status
                        </p>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${student.status === 'enrolled' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'bg-gray-100 text-gray-500'}`} data-id="element-2321">
                          {student.status === 'enrolled' ? <>
                              <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-2322" />
                              Enrolled
                            </> : 'Not Enrolled'}
                        </span>
                      </div>}
                    {student.leftOrganization !== undefined && <div data-id="element-2323">
                        <p className="text-text-tertiary mb-1" data-id="element-2324">
                          Organization Status
                        </p>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${student.leftOrganization ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]'}`} data-id="element-2325">
                          {student.leftOrganization ? <>
                              <XCircleIcon size={12} data-id="element-2326" />
                              Left Organization
                            </> : <>
                              <div className="w-2 h-2 rounded-full bg-[#00B75F]" data-id="element-2327" />
                              Active Member
                            </>}
                        </span>
                      </div>}
                    {student.createdAt && <div data-id="element-2328">
                        <p className="text-text-tertiary mb-1" data-id="element-2329">Created</p>
                        <p className="text-text-primary font-medium" data-id="element-2330">
                          {formatDate(student.createdAt)}
                        </p>
                      </div>}
                  </div>
                </div>}

              {/* Current Classroom Info */}
              {currentClassroom && <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-2331">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide" data-id="element-2332">
                    Current Enrollment
                  </h3>
                  <div className="space-y-2 text-sm" data-id="element-2333">
                    {student.joinedAt && <div data-id="element-2334">
                        <p className="text-text-tertiary text-xs" data-id="element-2335">Joined</p>
                        <p className="text-text-primary font-medium" data-id="element-2336">
                          {formatDate(student.joinedAt)}
                        </p>
                      </div>}
                    <div data-id="element-2337">
                      <p className="text-text-tertiary text-xs" data-id="element-2338">Classroom</p>
                      <p className="text-text-primary font-medium" data-id="element-2339">
                        {currentClassroom.name}
                      </p>
                    </div>
                    <div data-id="element-2340">
                      <p className="text-text-tertiary text-xs" data-id="element-2341">
                        Academic Year
                      </p>
                      <p className="text-text-primary font-medium" data-id="element-2342">
                        {currentClassroom.academicYear}
                      </p>
                    </div>
                  </div>
                </div>}

              {/* Guardian Contact */}
              <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-2343">
                <div className="flex items-center justify-between" data-id="element-2344">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide" data-id="element-2345">
                    Guardian Contact
                  </h3>
                  <button onClick={() => setIsEditContactModalOpen(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors" data-id="element-2346">
                    <EditIcon size={14} data-id="element-2347" />
                    Edit
                  </button>
                </div>
                {student.guardian ? <div className="bg-surface-elevated rounded-lg p-3 border border-custom" data-id="element-2348">
                    <div className="mb-3" data-id="element-2349">
                      <p className="text-sm font-semibold text-text-primary" data-id="element-2350">
                        {student.guardian.name}
                      </p>
                      <p className="text-xs text-text-tertiary mt-0.5" data-id="element-2351">
                        Contact for receiving student reports
                      </p>
                    </div>
                    <div className="space-y-2" data-id="element-2352">
                      {student.guardian.phone && <div className="flex items-center gap-2 text-text-secondary" data-id="element-2353">
                          <PhoneIcon size={12} data-id="element-2354" />
                          <a href={`tel:${student.guardian.phone}`} className="text-xs hover:text-text-primary transition-colors" data-id="element-2355">
                            {student.guardian.phone}
                          </a>
                        </div>}
                      {student.guardian.whatsapp && <div className="flex items-center gap-2 text-text-secondary" data-id="element-2356">
                          <PhoneIcon size={12} data-id="element-2357" />
                          <a href={`https://wa.me/${student.guardian.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-xs hover:text-text-primary transition-colors" data-id="element-2358">
                            {student.guardian.whatsapp} (WhatsApp)
                          </a>
                        </div>}
                    </div>
                  </div> : <div className="bg-surface-elevated rounded-lg p-3 border border-custom" data-id="element-2359">
                    <p className="text-sm text-text-tertiary text-center" data-id="element-2360">
                      No report contact added yet
                    </p>
                  </div>}
              </div>

              {/* Enrollment History */}
              {student.enrollments && student.enrollments.length > 0 && <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-2361">
                  <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide" data-id="element-2362">
                    Enrollment History
                  </h3>
                  <div className="space-y-2" data-id="element-2363">
                    {student.enrollments.sort((a, b) => {
                if (a.status === 'active' && b.status !== 'active') return -1;
                if (a.status !== 'active' && b.status === 'active') return 1;
                return new Date(b.enrolledAt).getTime() - new Date(a.enrolledAt).getTime();
              }).map(enrollment => <div key={enrollment.id} className="bg-surface-elevated rounded-lg p-3 border border-custom" data-id="element-2364">
                          <div className="flex items-start justify-between mb-2" data-id="element-2365">
                            <div data-id="element-2366">
                              <p className="text-sm font-semibold text-text-primary" data-id="element-2367">
                                {enrollment.classroomName}
                              </p>
                              <p className="text-xs text-text-tertiary" data-id="element-2368">
                                {enrollment.academicYear}
                              </p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${enrollment.status === 'active' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'bg-gray-100 text-gray-500'}`} data-id="element-2369">
                              {enrollment.status === 'active' ? <>
                                  <div className="w-1.5 h-1.5 rounded-full bg-[#00B75F]" data-id="element-2370" />
                                  Active
                                </> : 'Ended'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-text-secondary" data-id="element-2371">
                            <ClockIcon size={12} data-id="element-2372" />
                            <span data-id="element-2373">
                              {formatDate(enrollment.enrolledAt)}
                              {enrollment.leftAt && ` — ${formatDate(enrollment.leftAt)}`}
                            </span>
                          </div>
                        </div>)}
                  </div>
                </div>}

              {/* End Enrollment Action */}
              {showEndEnrollment && onEndEnrollment && <div className="pt-4 border-t border-custom" data-id="element-2374">
                  <button onClick={() => onEndEnrollment(student)} className="w-full px-4 py-3 bg-red-50 text-red-600 rounded-xl font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2 border border-red-200" data-id="element-2375">
                    <UserXIcon size={18} data-id="element-2376" />
                    End Enrollment for This Class
                  </button>
                  <p className="text-xs text-text-tertiary text-center mt-3" data-id="element-2377">
                    Ends enrollment in the current classroom. Student account
                    and all records are preserved.
                  </p>
                </div>}
            </div>
          </motion.div>

          {/* Edit Guardian Contact Modal */}
          <EditGuardianContactModal isOpen={isEditContactModalOpen} onClose={() => setIsEditContactModalOpen(false)} onSave={handleSaveGuardianContact} onRemove={student.guardian ? handleRemoveGuardianContact : undefined} studentName={student.name} currentContact={student.guardian ? {
        name: student.guardian.name,
        phone: student.guardian.phone || ''
      } : undefined} data-id="element-2378" />
        </>}
    </AnimatePresence>;
};